import { Card, Avatar, Button, Space, Skeleton } from '@arco-design/web-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { User } from '@/types'
import { renderQuota, formatNumberCompact } from '@/utils/quota'
import { renderQuotaWithUsage, renderRole, renderUserStatus, Pill } from '@/utils/render'

interface AccountOverviewCardProps {
  user: User | null
  loading?: boolean
  twoFaEnabled: boolean
  passkeyEnabled: boolean
}

export default function AccountOverviewCard({
  user,
  loading = false,
  twoFaEnabled,
  passkeyEnabled,
}: AccountOverviewCardProps) {
  const { t } = useTranslation()

  if (loading && !user) {
    return (
      <Card className="tt-card !rounded-2xl !border-[var(--console-border,#e2e8f0)]" title={t('personal.overview.title')}>
        <Skeleton animation text={{ rows: 4 }} />
      </Card>
    )
  }

  return (
    <Card
      className="tt-card !rounded-2xl !border-[var(--console-border,#e2e8f0)]"
      title={t('personal.overview.title')}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
        <div className="flex items-start gap-4 min-w-0">
          <Avatar size={72} className="flex-shrink-0 bg-[var(--brand-primary,#005447)]">
            {(user?.username?.[0] || 'U').toUpperCase()}
          </Avatar>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-[var(--console-text)] truncate">
                {user?.displayName || user?.username}
              </h2>
              {user && renderRole(user.role)}
              {user && renderUserStatus(user.status)}
              <Pill variant="info">{user?.group || 'default'}</Pill>
            </div>
            <p className="text-sm text-[var(--console-text-muted)] truncate">
              {user?.email || t('personal.overview.emailUnbound')}
            </p>
            <p className="text-xs text-[var(--console-text-subtle)] mt-1">{t('personal.overview.avatarTodo')}</p>
            <Space wrap className="mt-2">
              <Pill variant={twoFaEnabled ? 'success' : 'neutral'}>
                {twoFaEnabled ? t('personal.overview.twoFaOn') : t('personal.overview.twoFaOff')}
              </Pill>
              <Pill variant={passkeyEnabled ? 'success' : 'neutral'}>
                {passkeyEnabled ? t('personal.overview.passkeyOn') : t('personal.overview.passkeyOff')}
              </Pill>
            </Space>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 min-w-0">
          <div className="rounded-xl border border-[var(--console-border)] p-4 bg-[var(--console-page-bg,#f8fafa)]">
            <div className="text-xs text-[var(--console-text-muted)] mb-1">{t('personal.overview.balance')}</div>
            <div className="text-lg font-semibold tabular-nums">{renderQuotaWithUsage(user?.quota ?? 0, user?.usedQuota ?? 0)}</div>
          </div>
          <div className="rounded-xl border border-[var(--console-border)] p-4 bg-[var(--console-page-bg,#f8fafa)]">
            <div className="text-xs text-[var(--console-text-muted)] mb-1">{t('personal.overview.used')}</div>
            <div className="text-lg font-semibold tabular-nums">{renderQuota(user?.usedQuota)}</div>
          </div>
          <div className="rounded-xl border border-[var(--console-border)] p-4 bg-[var(--console-page-bg,#f8fafa)]">
            <div className="text-xs text-[var(--console-text-muted)] mb-1">{t('personal.overview.requests')}</div>
            <div className="text-lg font-semibold tabular-nums">{formatNumberCompact(user?.requestCount ?? 0)}</div>
          </div>
        </div>

        <div className="flex flex-col gap-2 w-full sm:w-[9.5rem] shrink-0">
          <Link to="/console/api-keys" className="block w-full">
            <Button type="outline" long className="!w-full">
              {t('personal.overview.quickApiKeys')}
            </Button>
          </Link>
          <Link to="/console/available-models" className="block w-full">
            <Button type="outline" long className="!w-full">
              {t('personal.overview.quickModels')}
            </Button>
          </Link>
          <Link to="/console/log" className="block w-full">
            <Button type="outline" long className="!w-full">
              {t('personal.overview.quickLogs')}
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  )
}
